// @ts-strict-ignore
import {
    AppMonitoringHealthMetricCategory,
    AppMonitoringHealthMetricType,
    AutomationTypesWithSettings,
    CadenceTargetDateSet,
    CompletedStepsTrigger,
    CustomMessageTemplateData,
    IntegrationInstallationId,
    MetricConditionOperator,
    NotificationDestination,
    ProfileOrCertificateType,
    RelativeTimeTrigger,
    ReleasePilotRotationItem,
    ReleaseStepBlockingConfig,
    ReleaseTypesForNonBlockingSteps,
    RolesMap,
    LifecycleFreezeSet,
    AutomationReleaseTypes,
    Percentage,
    MergeType,
    CommonNotification,
    RefreshType,
    FixRequestFormFieldSettings,
} from './../types';
import { AppSettingsUpdatable, AppSettingsV2Updatable } from './updatable';
import {
    AppSettings,
    BundleSizeAlertNotifyFor,
    BundleSizeIncreaseCondition,
    ChecklistItemPlacement,
    ChecklistItemReleaseTypes,
    ChecklistAttachment,
    ChecklistItemType,
    ExternalVersioningConfig,
    ExternalVersionType,
    NotificationFrequency,
    ReleaseMethod,
    ReleaseScheduleCadenceStyle,
    ReleaseStepType,
    ReleaseType,
    ReminderOffset,
} from '../types';
import { LocalizationUpdateRequest } from './release';
import {
    BrokenIntegrationDurationUnit,
    BrokenIntegrationFilterMode,
    BrokenIntegrationNotifyMode,
} from '../types/notifications';
import { BaseAuthedApiRequest } from '../api';
import { UpdateBetaTestingStyleRequest } from '@/actions/stepConfig';

export const UPDATE_APP_SETTINGS_SUCCESS = 'UPDATE_APP_SETTINGS_SUCCESS';
export const UPDATE_APP_SETTINGS_V2_SUCCESS = 'UPDATE_APP_SETTINGS_V2_SUCCESS';
export const TRY_REFRESH_INTEGRATIONS = 'TRY_REFRESH_INTEGRATIONS';

export interface BaseAppRequest extends BaseAuthedApiRequest {
    appId: string;
}

// Add release pilots
export interface AddReleasePilotsRequest extends BaseAppRequest {
    releasePilotIds: string[];
}

export interface RemoveReleasePilotRequest extends BaseAppRequest {
    releasePilotId: string;
}

export interface UpdateNotificationSettingsRequest extends BaseAppRequest {
    enabled: boolean;
    channels: string[];
    notificationId: string;
    mentionUsers: string[];
    mentionGroups: string[];
    hourOfDayForNotification?: number;
    appleProfileAndCertificateReminderDaysBeforeExpiration?: number;
    selectedProfileAndCertificateTypes?: ProfileOrCertificateType[];
    includeNewIssuesIdentifiedByRunway?: boolean;
    includeThreadRelatedNotifications?: boolean;
    adoptionThreshold?: number; // Legacy
    adoptionThresholds?: Record<IntegrationInstallationId, number>;
    phasedReleaseDayThreshold?: number;
    threadHealthMetrics?: boolean;
    emailsToInclude?: string[];
    notificationChannels?: NotificationDestination[];
    customCopy?: string;
    includeTicketListInSubmitNotifications?: boolean;
    bundleSizeIncreaseConditions?: BundleSizeIncreaseCondition[];
    bundleSizeCellularDownloadLimitThresholdMB?: number;
    bundleSizeAlertNotifyFor?: BundleSizeAlertNotifyFor;
    reminderOffsets?: ReminderOffset[];
    includeDirectDownloadLink?: boolean;
    directDownloadLinkAccessLimit?: number;
    directDownloadLinkExpiryHours?: number;
    includePROwnerInCherryPickNotifications?: boolean;
    includePRCommitInfo?: boolean;
    includeFixRequestDetails?: boolean;
    fixRequestReviewNotifyFor?: boolean;
    mentionReleasePilot?: boolean;
    notificationFrequency?: NotificationFrequency;
    brokenIntegrationNotifyMode?: BrokenIntegrationNotifyMode;
    brokenIntegrationFilterMode?: BrokenIntegrationFilterMode;
    brokenIntegrationInstallationIds?: IntegrationInstallationId[];
    brokenIntegrationDurationValue?: number;
    brokenIntegrationDurationUnit?: BrokenIntegrationDurationUnit;
}

//  Release schedule cadence
export interface ReleaseScheduleCadenceSettingsRequest extends BaseAppRequest {
    scheduleCadence: ReleaseScheduleCadenceStyle;
    cadenceTargetDateSets: CadenceTargetDateSet[];
    lifecycleFreezes: LifecycleFreezeSet[];
    cadenceTimezone?: string;
    skipNextPossibleSubmit: boolean;
    skipNextPossibleRelease: boolean;
    allowKickoffWhenPreviousNotComplete?: boolean;
    updateExistingReleaseTargetDates?: boolean;
}

export interface GetReleaseCalendarRequest extends BaseAppRequest {
    releaseId: string;
}

export interface GetWFReleaseCalendarRequest extends BaseAuthedApiRequest {
    workflowId: string;
    releaseId?: string;
}

// Automations

export interface BumpVersionAutomationData {
    strategy: string;
}

export interface HaltPhasedRolloutAutomationData {
    deltaValue: number;
}

export interface PromotePhasedRolloutAutomationData {
    deltaValue: number;
    adoptionThreshold: number;
}

export interface UpdateAutomationSettingsRequest extends BaseAppRequest {
    automationId: string;
    enabled: boolean;
    data?: AutomationTypesWithSettings['data'];
    configPerReleaseType?: Partial<
        Record<
            ReleaseTypesForNonBlockingSteps,
            {
                enabled?: boolean;
                data?: AutomationTypesWithSettings['data'];
                relativeTimeTrigger?: RelativeTimeTrigger;
                completedStepsTrigger?: CompletedStepsTrigger;
            }
        >
    >;
    releaseId?: string;
    relativeTimeTrigger: RelativeTimeTrigger;
    completedStepsTrigger: CompletedStepsTrigger;
    automationReleaseTypes: AutomationReleaseTypes;
}

// Feature Affiliations

export interface NewFeatureAffiliationRequest extends BaseAppRequest {
    orgId: string;
    integrationId: string;
    affiliationType: string;
    pattern: string;
}

export interface DeleteFeatureAffiliationRequest extends BaseAppRequest {
    featureAffiliationId: number;
}

export interface UpdateFeatureAffiliationPatternRequest extends BaseAppRequest {
    featureAffiliationId: number;
    pattern: string;
}

// Release Branch Patterns

export interface NewReleaseBranchPatternRequest extends BaseAppRequest {
    orgId: string;
    pattern: string;
    isHotfix: boolean;
}

export interface UpdateReleaseBranchPatternRequest extends BaseAppRequest {
    branchPatternId: number | string;
    pattern: string;
}

export interface DeleteReleaseBranchPatternRequest extends BaseAppRequest {
    branchPatternId: number;
}

// Release Tag Patterns

export interface NewReleaseTagPatternRequest extends BaseAppRequest {
    orgId: string;
    pattern: string;
}

export interface UpdateReleaseTagPatternRequest extends BaseAppRequest {
    tagPatternId: string | number;
    pattern: string;
}

export interface UpdateUserRolesMapRequest extends BaseAppRequest {
    rolesMap: RolesMap;
}

export interface TriggerIntegrationRefreshRequest extends BaseAppRequest {
    integrationId: IntegrationInstallationId;
    refreshType?: RefreshType;
}

// Monitoring Metrics

export interface AppMonitoringHealthMetricThreshold {
    value: number;
    condition: MetricConditionOperator;
}

export interface AddMonitoringMetricRequest extends BaseAppRequest {
    category: AppMonitoringHealthMetricCategory;
    thresholds: AppMonitoringHealthMetricThreshold[];
    type: AppMonitoringHealthMetricType;
    hideOnRolloutView: boolean;
    notifyOnApproachingThreshold: boolean;
    integrationInstallationId?: IntegrationInstallationId;
    eventName?: string;
    paramName?: string;
    displayName?: string;
    composedMetricIds?: string[];
    composeMetricsUsingEquation?: string;
    description?: string;
}

export interface UpdateMonitoringMetricRequest extends BaseAppRequest {
    metricId: string; // Sent as a path param?
    thresholds: AppMonitoringHealthMetricThreshold[];
    hideOnRolloutView: boolean;
    notifyOnApproachingThreshold: boolean;
    composedMetricIds?: string[];
    composeMetricsUsingEquation?: string;
    description?: string;
}

export interface DeleteMonitoringMetricRequest extends BaseAppRequest {
    metricId: string;
}

export interface DeleteReleaseTagPatternRequest extends BaseAppRequest {
    tagPatternId: number;
}

// Working branch

export interface SetAdditionalBranchesRequest extends BaseAppRequest {
    workingBranch: string;
    stagingBranch?: string;
    prodBranch?: string;
}

// provisioning profiles

export interface DeleteSigningKeyRequest extends BaseAppRequest {
    keyId: string;
}

export interface UpdateSigningKeyRequest extends BaseAppRequest {
    keyId: string;
    name: string;
}

export interface AddDeviceRequest extends BaseAppRequest {
    name: string;
    udid: string;
}

export interface AddUserDeviceRequest extends BaseAuthedApiRequest {
    userId: string;
    name: string;
    udid: string;
}

export interface UpdateShowSimplifiedBdUISettingsRequest extends BaseAuthedApiRequest {
    userId: string;
    enabled: boolean;
}

// Beta style

export interface UpdateBetaStyleRequest extends BaseAppRequest, UpdateBetaTestingStyleRequest {}

// Checklist Items

export interface ReleaseStepNewChecklistItemRequest extends BaseAppRequest {
    title: string;
    notifyEnabled: boolean;
    type: ChecklistItemType;
    placement: ChecklistItemPlacement;
    releaseStepType?: ReleaseStepType;
    description?: string;
    approverGroups: string[];
    owners: string[];
    releaseTypes: ChecklistItemReleaseTypes;
    oneOffForVersion?: string;
    releaseId?: string;
    targetCompletion?: RelativeTimeTrigger;
    incompleteReminderEnabled: boolean;
    autocompleteAtTargetCompletionEnabled: boolean;
    includeDescriptionInNotificationsEnabled: boolean;
    disableReminderAfterReleaseComplete: boolean;
    channels?: string[];
    attachments?: ChecklistAttachment[];
}

export interface ReleaseStepDeleteChecklistItemRequest extends BaseAppRequest {
    checklistItemId: string;
    releaseId?: string;
}

export interface ReleaseStepUpdateChecklistItemRequest extends BaseAppRequest {
    checklistItemId: string;
    title: string;
    notifyEnabled: boolean;
    description?: string;
    approverGroups: string[];
    owners: string[];
    releaseTypes: ChecklistItemReleaseTypes;
    incompleteReminderEnabled: boolean;
    autocompleteAtTargetCompletionEnabled: boolean;
    includeDescriptionInNotificationsEnabled: boolean;
    disableReminderAfterReleaseComplete: boolean;
    oneOffForVersion?: string;
    targetCompletion?: RelativeTimeTrigger;
    channels?: string[];
    attachments?: ChecklistAttachment[];
}

export interface UpdateCustomMessagesTemplatesRequest extends BaseAppRequest {
    customMessageTemplates: Pick<CustomMessageTemplateData, 'customStringType' | 'customStringValue'>[];
}

export interface UpdateChecklistItemsOrderRequest extends BaseAppRequest {
    orderedItemIds: string[];
    releaseId?: string;
}

export interface UpdateReleasePilotsOrderRequest extends BaseAppRequest {
    releasePilotRotation?: ReleasePilotRotationItem[];
    orderedUserIds?: string[];
}

export interface UpdateQualitativeOnboardingStatusRequest extends BaseAppRequest {
    isComplete: boolean;
}

export type EnableBuildDistroRequest = BaseAppRequest;

export interface UpdateAppUsersRequest extends BaseAppRequest {
    userIds: string[];
}

// Update App Store release settings

export interface UpdateAppleAppStoreReleaseDefaults extends BaseAppRequest {
    resetSummaryRating: boolean;
    shouldApplyDefaultEncryption: boolean;
    exportComplianceInfoUsesEncryption: boolean;
    releaseMethod: ReleaseMethod;
    phasedRelease: boolean;
}

export interface UpdatePlayAppStoreReleaseDefaults extends BaseAppRequest {
    stagedRelease: boolean;
    rolloutPercentage?: Percentage;
    // Deprecated in favor of rolloutPercentage
    initialStagedRolloutPercentage?: number;
}

export type UpdateHuaweiAppStoreReleaseDefaults = UpdatePlayAppStoreReleaseDefaults;

// App Store Default Release Notes

export interface UpdateDefaultReleaseNotes extends BaseAppRequest {
    defaultReleaseNotes: Pick<LocalizationUpdateRequest, 'whatsNew' | 'locale'>[];
}

export interface UpdateVersioningDefaultsRequest extends BaseAppRequest {
    defaultReleaseType: ReleaseType;
    assumePointReleasesAreHotfixes: boolean;
    externalVersioningConfigForType?: Partial<Record<ExternalVersionType, ExternalVersioningConfig>>;
}

// Bump version files

export interface UpdateBumpVersionOverrideFilesRequest extends BaseAppRequest {
    files: string[];
}

// update non-blocking steps

export interface UpdateNonBlockingStepsRequest extends BaseAppRequest {
    releaseStepsBlockingConfigForReleaseType: Record<
        ReleaseTypesForNonBlockingSteps,
        Record<ReleaseStepType, ReleaseStepBlockingConfig>
    >;
    shouldHideNonBlockingSteps: boolean;
}

// update code filter paths

export interface UpdateFilterPathsRequest extends BaseAppRequest {
    filterPaths: string[];
}

// Setup requests

export interface GetCandidateSelectableCommitsRequest extends BaseAppRequest {
    commitHash?: string;
    version?: string;
}

export interface GetAppRepoContributorsRequest extends BaseAppRequest {
    bucketId?: string;
}

export interface TagCommitRequest extends BaseAppRequest {
    version: string;
    tagName?: string;
    commitHash: string;
}

// File Requests

export interface UploadDefaultReviewAttachmentRequest extends BaseAppRequest {
    file: File;
    stepConfigId?: string;
}

export interface RemoveDefaultReviewAttachmentRequest extends BaseAppRequest {
    fileName: string;
    stepConfigId?: string;
}

export type UpdateSettingsRequestData =
    | NewFeatureAffiliationRequest
    | DeleteFeatureAffiliationRequest
    | UpdateFeatureAffiliationPatternRequest
    | NewReleaseBranchPatternRequest
    | UpdateReleaseBranchPatternRequest
    | DeleteReleaseBranchPatternRequest
    | NewReleaseTagPatternRequest
    | UpdateReleaseTagPatternRequest
    | DeleteReleaseTagPatternRequest
    | ReleaseStepNewChecklistItemRequest
    | ReleaseStepUpdateChecklistItemRequest
    | UpdateChecklistItemsOrderRequest
    | ReleaseStepDeleteChecklistItemRequest
    | UpdateBetaStyleRequest
    | SetAdditionalBranchesRequest
    | UpdateAppleAppStoreReleaseDefaults
    | UpdatePlayAppStoreReleaseDefaults
    | UpdateHuaweiAppStoreReleaseDefaults
    | UpdateDefaultReleaseNotes
    | UpdateAutomationSettingsRequest
    | ReleaseScheduleCadenceSettingsRequest
    | AddReleasePilotsRequest
    | RemoveReleasePilotRequest
    | UpdateVersioningDefaultsRequest
    | UpdateNotificationSettingsRequest
    | UpdateBumpVersionOverrideFilesRequest
    | UpdateNonBlockingStepsRequest
    | UpdateFilterPathsRequest
    | TagCommitRequest
    | UploadDefaultReviewAttachmentRequest
    | RemoveDefaultReviewAttachmentRequest
    | UpdateUserRolesMapRequest
    | TriggerIntegrationRefreshRequest
    | UpdateAppUsersRequest
    | UpdateMonitoringMetricRequest
    | UpdateCustomMessagesTemplatesRequest
    | AddMonitoringMetricRequest
    | DeleteMonitoringMetricRequest
    | EnableBuildDistroRequest
    | UpdateQualitativeOnboardingStatusRequest
    | UpdateReleasePilotsOrderRequest
    | UpdateTranslationDirectoriesRequest
    | UpdateFixesFeatureSettingsRequest
    | UpdateBundleSizeDeviceModelRequest
    | UpdateStepNotificationConfigRequest
    | DeleteStepNotificationConfigRequest;

export interface UpdateSettingsRequest {
    requestData: UpdateSettingsRequestData;
    id?: number | string; // the identifier for the instance the settings request is being applied to, in case there can be multiple instances eg. feature affiliations
}

export interface UpdateAppSettingsResponse {
    appId: string;
    appSettings: AppSettings;
}

// Actions

export interface TryUpdateAppSettingsAction {
    type: AppSettingsUpdatable;
    payload: UpdateSettingsRequest;
}

export interface UpdateAppSettingsSuccessAction {
    type: typeof UPDATE_APP_SETTINGS_SUCCESS;
    payload: UpdateAppSettingsResponse;
}

export interface TryUpdateAppSettingsV2Action {
    type: AppSettingsV2Updatable;
    payload: UpdateSettingsRequest;
}

export interface TryRefreshIntegrationsAction {
    type: typeof TRY_REFRESH_INTEGRATIONS;
    payload: BaseAppRequest;
}

// Action Creators

export const tryRefreshIntegrations: (requestData: BaseAppRequest) => TryRefreshIntegrationsAction = (requestData) => {
    return {
        type: TRY_REFRESH_INTEGRATIONS,
        payload: requestData,
    };
};

export const tryUpdateAppSettings: (
    type: AppSettingsUpdatable,
    requestData: UpdateSettingsRequestData,
    id?: number | string,
) => TryUpdateAppSettingsAction = (type, requestData, id) => {
    return {
        type: type,
        payload: {
            requestData,
            id: id ?? null,
        },
    };
};

export const updateAppSettingsSuccessAction: (payload: UpdateAppSettingsResponse) => UpdateAppSettingsSuccessAction = (
    payload,
) => {
    return {
        type: UPDATE_APP_SETTINGS_SUCCESS,
        payload,
    };
};

// use for endpoints that return releases affected by app settings change

export const tryUpdateAppSettingsV2: (
    type: AppSettingsV2Updatable,
    requestData: UpdateSettingsRequestData,
    id?: number | string,
) => TryUpdateAppSettingsV2Action = (type, requestData, id) => {
    return {
        type: type,
        payload: {
            requestData,
            id: id ?? null,
        },
    };
};

export interface UpdateTranslationDirectoriesRequest extends BaseAppRequest {
    directories: string[];
}

// update requiresFixRequestsApprovals

// Every setting is optional because the fix-settings UI is split across several modals, each of
// which sends only the fields it edits.
export interface UpdateFixesFeatureSettingsRequest extends BaseAppRequest {
    addFixRequestsToOpenPRsAgainstReleaseBranches?: boolean;
    requiresFixRequestsApprovals?: boolean;
    requiredFixRequestsApprovals?: number;
    enableStatusChecksForFixRequests?: boolean;
    fixRequestFormFieldsSettings?: FixRequestFormFieldSettings[];
    threadFixNotifications?: boolean;
    mergeUserCreatedPRs?: boolean;
    workingBranchUserCreatedPRMergeType?: MergeType;
    releaseBranchUserCreatedPRMergeType?: MergeType;
    preventFixRequestsAfterSubmission?: boolean;
    preventFixRequestsAfterSubmissionCustomCopyEnabled?: boolean;
    preventFixRequestsAfterSubmissionCustomCopy?: string;
}

export interface UpdateBundleSizeDeviceModelRequest extends BaseAppRequest {
    bundleSizeDeviceModelId: string;
}

// Step notification config overrides
export interface UpdateStepNotificationConfigRequest extends BaseAppRequest {
    workflowId: string;
    stepConfigId: string;
    notificationId: string;
    enabled?: boolean;
    settings?: Partial<CommonNotification>;
    channels?: string[];
}

export interface DeleteStepNotificationConfigRequest extends BaseAppRequest {
    workflowId: string;
    stepConfigId: string;
    notificationId: string;
}
